/**
 * marketplace.jsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Admin marketplace management — manage trading signals, bots, indicators
 * and strategies. View subscriptions, add / edit / delete items.
 * Route: /admin/marketplace
 *
 * Tables used:
 *   marketplace_items         (id, slug, type, name, tag, description,
 *                              price_monthly, badge_label, badge_type,
 *                              icon_class, color_hex, delivery_method,
 *                              markets, roi_12m_pct, win_rate_pct,
 *                              total_trades, accuracy_pct,
 *                              subscriber_count, is_active, created_at)
 *   marketplace_subscriptions (id, user_id → users, item_id → marketplace_items,
 *                              status, pnl_30d, renews_at, started_at,
 *                              cancelled_at, item_type)
 *   users                     (id, first_name, last_name, email)
 */

import { useState, useEffect, useMemo, useCallback } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import supabase from "../supabase";
import {
  // sidebar
  LayoutDashboard, Wallet, Receipt, Copy, UserCheck,
  HeadphonesIcon, Bell, CandlestickChart, BarChart2, ShoppingBag,
  ChevronRight, LogOut, Users,CreditCard, MapPin,
  // page
  Search, RefreshCw, Eye, X, Plus, Trash2, Pencil,
  AlertCircle, CheckCircle2, ChevronUp, ChevronDown,
  Tag, Bot, Zap, TrendingUp, Activity, XCircle, Menu,
} from "lucide-react";

const PER_PAGE = 12;

// ─── Sidebar nav ──────────────────────────────────────────────────────────────
const NAV_SECTIONS = [
  {
    label: "Overview",
    items: [{ to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true }],
  },
  {
    label: "Users & Finance",
    items: [
      { to: "/admin/users",        label: "Users",        icon: Users   },
      { to: "/admin/wallets",      label: "Wallets",      icon: Wallet  },
      { to: "/admin/transactions", label: "Transactions", icon: Receipt },
    ],
  },
  {
    label: "Trading",
    items: [
      { to: "/admin/copy-trades",  label: "Copy trades",   icon: Copy             },
      { to: "/admin/traders",      label: "Hired traders", icon: UserCheck        },
      { to: "/admin/trading",      label: "Open trades",   icon: CandlestickChart },
      { to: "/admin/stock-trades", label: "Stock trades",  icon: BarChart2        },
      { to: "/admin/marketplace",  label: "Marketplace",   icon: ShoppingBag      },
    ],
  },
  {
    label: "Communication",
    items: [
      { to: "/admin/support",       label: "Support",       icon: HeadphonesIcon },
      { to: "/admin/notifications", label: "Notifications", icon: Bell           },
    ],
  },
   {
    label: "Settings",
    items: [
      { to: "/admin/subscriptions",      label: "Subscriptions",      icon: CreditCard },
      { to: "/admin/payment-addresses",  label: "Payment Addresses",  icon: MapPin     },
      { to: "/admin/market-subscriptions",  label: "Market Subscriptions",  icon: MapPin     },

    ],
  },
];

const ITEM_TYPES = ["signal", "bot", "indicator", "strategy", "other"];

const TYPE_META = {
  signal:    { Icon: Zap,       color: "#6366f1", label: "Signal"    },
  bot:       { Icon: Bot,       color: "#34d399", label: "Bot"       },
  indicator: { Icon: BarChart2, color: "#fbbf24", label: "Indicator" },
  strategy:  { Icon: TrendingUp,color: "#a78bfa", label: "Strategy"  },
  other:     { Icon: ShoppingBag,color:"#7878a0", label: "Other"     },
};

// ─── Styles ───────────────────────────────────────────────────────────────────
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  :root {
    --bg-base:      #06060f;
    --bg-surface:   #0a0a16;
    --bg-elevated:  #0e0e1c;
    --bg-card:      #111122;
    --bg-hover:     #14142a;
    --border:       rgba(255,255,255,0.055);
    --border-hi:    rgba(255,255,255,0.11);
    --accent:       #6366f1;
    --accent-light: #818cf8;
    --accent-dim:   rgba(99,102,241,0.13);
    --accent-glow:  rgba(99,102,241,0.22);
    --green:        #34d399;
    --green-dim:    rgba(52,211,153,0.13);
    --amber:        #fbbf24;
    --amber-dim:    rgba(251,191,36,0.13);
    --rose:         #fb7185;
    --rose-dim:     rgba(251,113,133,0.13);
    --cyan:         #22d3ee;
    --purple:       #a78bfa;
    --purple-dim:   rgba(167,139,250,0.13);
    --text-primary:   #eeeeff;
    --text-secondary: #7878a0;
    --text-muted:     #3e3e58;
    --font-sans: 'Inter', system-ui, sans-serif;
    --font-mono: 'JetBrains Mono', monospace;
    --sidebar-w: 232px;
    --topbar-h:  60px;
    --radius-sm: 8px;
    --radius-md: 14px;
    --radius-lg: 18px;
  }
  html, body, #root { height: 100%; background: var(--bg-base); font-family: var(--font-sans); color-scheme: dark; }
  html { scroll-behavior: smooth; }

  /* Shell */
  .mp-shell { display: flex; height: 100vh; overflow: hidden; background: var(--bg-base); position: relative; }
  .mp-shell::before {
    content: ''; position: fixed; inset: 0; pointer-events: none; z-index: 0;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='1'/%3E%3C/svg%3E");
    opacity: 0.018;
  }

  /* Sidebar */
  .ad-sidebar {
    width: var(--sidebar-w); flex-shrink: 0; display: flex; flex-direction: column;
    background: var(--bg-surface); border-right: 1px solid var(--border);
    position: relative; z-index: 10; height: 100vh; min-height: 0;
  }
  .ad-sidebar::after {
    content: ''; position: absolute; top: 0; right: 0; bottom: 0; width: 1px;
    background: linear-gradient(to bottom, transparent 0%, var(--accent-glow) 35%, rgba(99,102,241,0.08) 70%, transparent 100%);
    pointer-events: none;
  }
  .ad-logo { display: flex; align-items: center; gap: 11px; padding: 0 18px; height: var(--topbar-h); border-bottom: 1px solid var(--border); flex-shrink: 0; }
  .ad-logo-icon {
    width: 32px; height: 32px; border-radius: 10px;
    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a78bfa 100%);
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
    box-shadow: 0 0 0 1px rgba(99,102,241,0.4), 0 4px 16px rgba(99,102,241,0.35);
  }
  .ad-logo-text strong { font-size: 13.5px; font-weight: 800; letter-spacing: -0.4px; background: linear-gradient(to right, #eeeeff, #a5b4fc); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
  .ad-logo-text span   { font-size: 9.5px; color: var(--text-muted); display: block; margin-top: 3px; letter-spacing: 0.8px; text-transform: uppercase; font-weight: 500; }
  .ad-nav { flex: 1; min-height: 0; overflow-y: auto; overscroll-behavior: contain; scroll-behavior: smooth; -webkit-overflow-scrolling: touch; padding: 18px 10px; display: flex; flex-direction: column; gap: 22px; }
  .ad-nav::-webkit-scrollbar { width: 0; }
  .ad-nav-section-label { font-size: 9px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; color: var(--text-muted); padding: 0 10px; margin-bottom: 5px; }
  .ad-nav-items { display: flex; flex-direction: column; gap: 1px; }
  .ad-nav-link { display: flex; align-items: center; gap: 9px; padding: 8px 10px; border-radius: var(--radius-sm); font-size: 12.5px; font-weight: 500; color: var(--text-secondary); text-decoration: none; transition: all 0.18s ease; position: relative; }
  .ad-nav-link:hover { background: var(--bg-hover); color: var(--text-primary); }
  .ad-nav-link.active { background: linear-gradient(135deg, rgba(99,102,241,0.18) 0%, rgba(139,92,246,0.10) 100%); color: var(--accent-light); font-weight: 600; box-shadow: inset 0 0 0 1px rgba(99,102,241,0.15); }
  .ad-nav-link.active::before { content: ''; position: absolute; left: 0; top: 18%; bottom: 18%; width: 2.5px; border-radius: 99px; background: linear-gradient(to bottom, var(--accent), var(--purple)); box-shadow: 0 0 10px var(--accent-glow); }
  .ad-nav-link svg { flex-shrink: 0; opacity: 0.65; transition: opacity 0.15s; }
  .ad-nav-link:hover svg, .ad-nav-link.active svg { opacity: 1; }
  .ad-nav-chevron { margin-left: auto; opacity: 0.35; }
  .ad-user-card { flex-shrink: 0; border-top: 1px solid var(--border); padding: 12px 10px; background: linear-gradient(to top, rgba(99,102,241,0.04) 0%, transparent 100%); }
  .ad-user-inner { display: flex; align-items: center; gap: 10px; padding: 9px 10px; border-radius: var(--radius-sm); transition: background 0.15s; }
  .ad-user-inner:hover { background: var(--bg-hover); }
  .ad-user-avatar { width: 32px; height: 32px; border-radius: 50%; background: linear-gradient(135deg, var(--accent-dim), rgba(139,92,246,0.2)); border: 1.5px solid rgba(99,102,241,0.35); display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 700; color: var(--accent-light); flex-shrink: 0; }
  .ad-user-info { flex: 1; min-width: 0; }
  .ad-user-info strong { font-size: 12px; font-weight: 600; color: var(--text-primary); display: block; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .ad-user-info span   { font-size: 10px; color: var(--text-muted); display: block; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-top: 1px; }
  .ad-logout-btn { width: 28px; height: 28px; border-radius: 7px; background: none; border: none; cursor: pointer; display: flex; align-items: center; justify-content: center; color: var(--text-muted); transition: all 0.15s; }
  .ad-logout-btn:hover { background: var(--rose-dim); color: var(--rose); }

  /* Page column */
  .mp-page { flex: 1; display: flex; flex-direction: column; overflow: hidden; position: relative; z-index: 1; }

  /* Topbar */
  .mp-topbar { flex-shrink: 0; height: var(--topbar-h); background: rgba(10,10,22,0.85); backdrop-filter: blur(12px); border-bottom: 1px solid var(--border); display: flex; align-items: center; justify-content: space-between; padding: 0 28px; position: relative; }
  .mp-topbar::after { content: ''; position: absolute; bottom: -1px; left: 0; right: 0; height: 1px; background: linear-gradient(to right, transparent 0%, var(--accent-glow) 50%, transparent 100%); }
  .mp-topbar-left  { display: flex; align-items: center; gap: 14px; }
  .mp-topbar-right { display: flex; align-items: center; gap: 8px; }
  .mp-topbar-badge { display: flex; align-items: center; gap: 6px; padding: 3px 10px; border-radius: 99px; background: var(--accent-dim); border: 1px solid rgba(99,102,241,0.2); font-size: 10px; font-weight: 700; color: var(--accent-light); letter-spacing: 0.5px; text-transform: uppercase; }
  .mp-topbar-title { font-size: 15px; font-weight: 700; color: var(--text-primary); letter-spacing: -0.4px; }
  .mp-topbar-sub   { font-size: 11px; color: var(--text-muted); margin-top: 1px; }

  /* Buttons */
  .mp-btn { display: flex; align-items: center; gap: 6px; padding: 6px 14px; border-radius: var(--radius-sm); font-size: 11.5px; font-weight: 500; cursor: pointer; transition: all 0.18s; font-family: var(--font-sans); border: 1px solid var(--border-hi); background: var(--bg-card); color: var(--text-secondary); }
  .mp-btn:hover { background: var(--accent-dim); color: var(--accent-light); border-color: rgba(99,102,241,0.3); }
  .mp-btn-accent { background: linear-gradient(135deg, rgba(99,102,241,0.22) 0%, rgba(139,92,246,0.15) 100%); color: var(--accent-light); border-color: rgba(99,102,241,0.3); }
  .mp-btn-accent:hover { background: linear-gradient(135deg, rgba(99,102,241,0.32) 0%, rgba(139,92,246,0.22) 100%); }

  /* Main */
  .mp-main { flex: 1; overflow-y: auto; scroll-behavior: smooth; -webkit-overflow-scrolling: touch; padding: 26px 28px; display: flex; flex-direction: column; gap: 20px; }
  .mp-main::-webkit-scrollbar { width: 4px; }
  .mp-main::-webkit-scrollbar-thumb { background: rgba(99,102,241,0.25); border-radius: 99px; }

  /* Stat grid */
  .mp-stat-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 14px; }
  .mp-stat { background: var(--bg-card); border: 1px solid var(--border); border-radius: var(--radius-md); padding: 18px 20px; display: flex; flex-direction: column; gap: 12px; position: relative; overflow: hidden; transition: border-color 0.22s, box-shadow 0.22s, transform 0.18s; }
  .mp-stat::before { content: ''; position: absolute; inset: 0; background: radial-gradient(ellipse at 110% -10%, var(--stat-glow, transparent) 0%, transparent 60%); pointer-events: none; }
  .mp-stat::after  { content: ''; position: absolute; bottom: 0; left: 0; right: 0; height: 1px; background: linear-gradient(to right, transparent 20%, var(--stat-bar, transparent) 50%, transparent 80%); pointer-events: none; }
  .mp-stat:hover { border-color: rgba(255,255,255,0.1); box-shadow: 0 4px 24px rgba(0,0,0,0.4); transform: translateY(-1px); }
  .mp-stat-top   { display: flex; align-items: flex-start; justify-content: space-between; }
  .mp-stat-label { font-size: 9.5px; font-weight: 700; letter-spacing: 1.1px; text-transform: uppercase; color: var(--text-muted); }
  .mp-stat-icon  { width: 32px; height: 32px; border-radius: 9px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
  .mp-stat-value { font-size: 26px; font-weight: 700; color: var(--text-primary); letter-spacing: -1.5px; font-family: var(--font-mono); line-height: 1; }
  .mp-stat-sub   { font-size: 10.5px; color: var(--text-muted); margin-top: 4px; }

  /* Filter pills */
  .mp-filter-wrap { display: flex; gap: 2px; padding: 3px; background: var(--bg-surface); border: 1px solid var(--border); border-radius: var(--radius-sm); }
  .mp-filter-pill { padding: 4px 11px; border-radius: 6px; font-size: 11px; font-weight: 500; cursor: pointer; border: none; background: none; font-family: var(--font-sans); color: var(--text-muted); transition: all 0.15s; text-transform: capitalize; }
  .mp-filter-pill:hover { color: var(--text-secondary); }
  .mp-filter-pill.active { background: var(--accent-dim); color: var(--accent-light); }

  /* Table card */
  .mp-table-card { background: var(--bg-card); border: 1px solid var(--border); border-radius: var(--radius-md); overflow: hidden; }
  .mp-table-head { display: flex; align-items: center; justify-content: space-between; padding: 14px 20px; border-bottom: 1px solid var(--border); gap: 12px; flex-wrap: wrap; }
  .mp-table-title { font-size: 13px; font-weight: 600; color: var(--text-primary); }
  .mp-table-count { font-size: 10px; color: var(--text-muted); background: rgba(255,255,255,0.05); border: 1px solid var(--border); border-radius: 99px; padding: 2px 8px; }
  .mp-search-wrap { position: relative; }
  .mp-search-icon { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); color: var(--text-muted); pointer-events: none; }
  .mp-search { padding: 7px 12px 7px 30px; background: var(--bg-surface); border: 1px solid var(--border); border-radius: var(--radius-sm); font-size: 12px; color: var(--text-primary); font-family: var(--font-sans); outline: none; width: 200px; transition: border-color 0.18s; }
  .mp-search::placeholder { color: var(--text-muted); }
  .mp-search:focus { border-color: rgba(99,102,241,0.45); box-shadow: 0 0 0 3px rgba(99,102,241,0.08); }

  /* Table */
  .mp-table { width: 100%; border-collapse: collapse; }
  .mp-th { padding: 10px 16px; text-align: left; font-size: 9.5px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; color: var(--text-muted); border-bottom: 1px solid var(--border); cursor: pointer; white-space: nowrap; user-select: none; transition: color 0.15s; }
  .mp-th:hover { color: var(--text-secondary); }
  .mp-th.no-sort { cursor: default; }
  .mp-th.right { text-align: right; }
  .mp-td { padding: 13px 16px; border-bottom: 1px solid rgba(255,255,255,0.03); vertical-align: middle; }
  .mp-tr { transition: background 0.15s; }
  .mp-tr:hover { background: rgba(255,255,255,0.018); }
  .mp-tr:last-child .mp-td { border-bottom: none; }

  /* Item icon */
  .mp-item-icon { width: 34px; height: 34px; border-radius: 10px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }

  /* Chips */
  .mp-chip { display: inline-flex; align-items: center; gap: 4px; padding: 2px 8px; border-radius: 99px; font-size: 10px; font-weight: 600; }
  .mp-chip-active   { background: var(--green-dim);   color: var(--green);  border: 1px solid rgba(52,211,153,0.25);  }
  .mp-chip-inactive { background: rgba(255,255,255,0.05); color: var(--text-muted); border: 1px solid var(--border); }
  .mp-chip-sub-active    { background: var(--green-dim);   color: var(--green);  border: 1px solid rgba(52,211,153,0.25);  }
  .mp-chip-sub-cancelled { background: var(--rose-dim);    color: var(--rose);   border: 1px solid rgba(251,113,133,0.25); }
  .mp-chip-sub-paused    { background: var(--amber-dim);   color: var(--amber);  border: 1px solid rgba(251,191,36,0.25);  }

  /* Avatar */
  .mp-avatar { width: 30px; height: 30px; border-radius: 50%; flex-shrink: 0; display: flex; align-items: center; justify-content: center; font-size: 10px; font-weight: 700; }
  .mp-avatar-indigo { background: rgba(99,102,241,0.18); color: #818cf8; }
  .mp-avatar-green  { background: rgba(52,211,153,0.18); color: #34d399; }
  .mp-avatar-amber  { background: rgba(251,191,36,0.18);  color: #fbbf24; }
  .mp-avatar-rose   { background: rgba(251,113,133,0.18); color: #fb7185; }
  .mp-avatar-purple { background: rgba(167,139,250,0.18); color: #a78bfa; }

  /* Icon buttons */
  .mp-icon-btn { width: 28px; height: 28px; border-radius: 7px; border: 1px solid var(--border); background: none; cursor: pointer; display: flex; align-items: center; justify-content: center; color: var(--text-muted); transition: all 0.15s; }
  .mp-icon-btn:hover         { background: var(--accent-dim); color: var(--accent-light); border-color: rgba(99,102,241,0.3); }
  .mp-icon-btn.edit:hover    { background: var(--accent-dim); color: var(--accent-light); border-color: rgba(99,102,241,0.3); }
  .mp-icon-btn.delete:hover  { background: var(--rose-dim);   color: var(--rose);         border-color: rgba(251,113,133,0.3); }

  /* Pagination */
  .mp-pagination { display: flex; align-items: center; justify-content: space-between; padding: 12px 20px; border-top: 1px solid var(--border); }
  .mp-page-info  { font-size: 11px; color: var(--text-muted); }
  .mp-page-btns  { display: flex; gap: 3px; }
  .mp-page-btn   { width: 28px; height: 28px; border-radius: 6px; border: 1px solid var(--border); background: none; cursor: pointer; font-size: 11px; color: var(--text-muted); font-family: var(--font-sans); display: flex; align-items: center; justify-content: center; transition: all 0.15s; }
  .mp-page-btn:hover { background: var(--bg-hover); color: var(--text-primary); }
  .mp-page-btn.active { background: var(--accent-dim); color: var(--accent-light); border-color: rgba(99,102,241,0.3); }

  /* Empty */
  .mp-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 52px 20px; gap: 10px; color: var(--text-muted); }
  .mp-empty-icon { width: 40px; height: 40px; border-radius: 12px; background: rgba(255,255,255,0.04); border: 1px solid var(--border); display: flex; align-items: center; justify-content: center; }

  /* Skeleton */
  .mp-skel { background: rgba(255,255,255,0.05); border-radius: 4px; animation: mp-pulse 1.4s ease-in-out infinite; }
  @keyframes mp-pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }

  /* Modal overlay */
  .mp-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.65); backdrop-filter: blur(4px); display: flex; align-items: center; justify-content: center; z-index: 50; padding: 20px; }
  .mp-modal { background: var(--bg-elevated); border: 1px solid var(--border-hi); border-radius: var(--radius-lg); width: 100%; box-shadow: 0 24px 80px rgba(0,0,0,0.6); overflow: hidden; }
  .mp-modal-head { display: flex; align-items: flex-start; justify-content: space-between; padding: 20px 24px 18px; border-bottom: 1px solid var(--border); gap: 12px; }
  .mp-modal-title { font-size: 15px; font-weight: 700; color: var(--text-primary); letter-spacing: -0.3px; }
  .mp-modal-sub   { font-size: 12px; color: var(--text-secondary); margin-top: 3px; }
  .mp-modal-body  { padding: 22px 24px; display: flex; flex-direction: column; gap: 14px; overflow-y: auto; scroll-behavior: smooth; -webkit-overflow-scrolling: touch; max-height: 68vh; }
  .mp-modal-foot  { display: flex; align-items: center; justify-content: flex-end; gap: 8px; padding: 16px 24px; border-top: 1px solid var(--border); }
  .mp-close-btn { width: 28px; height: 28px; border-radius: 7px; background: none; border: 1px solid var(--border); display: flex; align-items: center; justify-content: center; color: var(--text-muted); cursor: pointer; transition: all 0.15s; flex-shrink: 0; }
  .mp-close-btn:hover { background: var(--rose-dim); color: var(--rose); border-color: rgba(251,113,133,0.25); }

  /* Form */
  .mp-form-row   { display: flex; flex-direction: column; gap: 5px; }
  .mp-form-label { font-size: 10px; font-weight: 700; letter-spacing: 0.8px; text-transform: uppercase; color: var(--text-muted); }
  .mp-form-input, .mp-form-select, .mp-form-textarea {
    padding: 8px 12px; background: var(--bg-surface); border: 1px solid var(--border);
    border-radius: var(--radius-sm); font-size: 12.5px; color: var(--text-primary);
    font-family: var(--font-sans); outline: none; transition: border-color 0.18s; width: 100%;
  }
  .mp-form-textarea { resize: vertical; min-height: 72px; }
  .mp-form-input::placeholder, .mp-form-textarea::placeholder { color: var(--text-muted); }
  .mp-form-input:focus, .mp-form-select:focus, .mp-form-textarea:focus { border-color: rgba(99,102,241,0.45); box-shadow: 0 0 0 3px rgba(99,102,241,0.08); }
  .mp-form-select option { background: var(--bg-elevated); }
  .mp-type-pills { display: flex; gap: 6px; flex-wrap: wrap; }
  .mp-type-pill { padding: 5px 12px; border-radius: 99px; font-size: 11px; font-weight: 600; cursor: pointer; border: 1px solid var(--border); background: none; font-family: var(--font-sans); color: var(--text-muted); transition: all 0.15s; text-transform: capitalize; }
  .mp-type-pill.active { border-color: rgba(99,102,241,0.4); background: var(--accent-dim); color: var(--accent-light); }

  /* Save / ghost / danger buttons */
  .mp-save-btn { display: flex; align-items: center; gap: 6px; padding: 7px 18px; border-radius: var(--radius-sm); background: linear-gradient(135deg, var(--accent) 0%, #8b5cf6 100%); border: none; color: #fff; font-size: 12px; font-weight: 600; cursor: pointer; font-family: var(--font-sans); transition: opacity 0.18s; box-shadow: 0 4px 14px rgba(99,102,241,0.35); }
  .mp-save-btn:hover:not(:disabled) { opacity: 0.88; }
  .mp-save-btn:disabled { opacity: 0.45; cursor: not-allowed; }
  .mp-ghost-btn { display: flex; align-items: center; gap: 6px; padding: 7px 16px; border-radius: var(--radius-sm); background: none; border: 1px solid var(--border-hi); color: var(--text-secondary); font-size: 12px; font-weight: 500; cursor: pointer; transition: all 0.15s; font-family: var(--font-sans); }
  .mp-ghost-btn:hover { background: var(--bg-hover); color: var(--text-primary); }
  .mp-danger-btn { display: flex; align-items: center; gap: 6px; padding: 7px 16px; border-radius: var(--radius-sm); background: var(--rose-dim); border: 1px solid rgba(251,113,133,0.3); color: var(--rose); font-size: 12px; font-weight: 600; cursor: pointer; font-family: var(--font-sans); transition: all 0.15s; }
  .mp-danger-btn:hover:not(:disabled) { background: rgba(251,113,133,0.2); }
  .mp-danger-btn:disabled { opacity: 0.45; cursor: not-allowed; }

  /* Section label inside modal */
  .mp-section-label { font-size: 9.5px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; color: var(--text-muted); margin-bottom: 8px; padding-bottom: 6px; border-bottom: 1px solid var(--border); }

  /* Sub list item */
  .mp-sub-row { display: flex; align-items: center; gap: 12px; padding: 11px 20px; border-bottom: 1px solid rgba(255,255,255,0.03); transition: background 0.15s; }
  .mp-sub-row:hover { background: rgba(255,255,255,0.015); }
  .mp-sub-row:last-child { border-bottom: none; }

  /* Toast */
  .mp-toast { position: fixed; bottom: 24px; right: 24px; z-index: 100; display: flex; align-items: center; gap: 10px; padding: 12px 18px; border-radius: var(--radius-md); background: var(--bg-elevated); border: 1px solid var(--border-hi); box-shadow: 0 8px 32px rgba(0,0,0,0.5); font-size: 13px; font-weight: 500; color: var(--text-primary); animation: mp-slide-in 0.22s ease; }
  @keyframes mp-slide-in { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }

  /* Gate */
  .mp-gate { height: 100vh; display: flex; align-items: center; justify-content: center; flex-direction: column; gap: 14px; background: var(--bg-base); }
  .mp-gate-icon { width: 36px; height: 36px; border-radius: 10px; background: linear-gradient(135deg,#6366f1,#a78bfa); box-shadow: 0 0 24px rgba(99,102,241,0.4); display: flex; align-items: center; justify-content: center; }

  /* Form grids (responsive) */
  .mp-form-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
  .mp-form-grid-3 { display: grid; grid-template-columns: 1fr 1fr 100px; gap: 10px; }
  .mp-form-grid-4 { display: grid; grid-template-columns: 1fr 1fr 1fr 1fr; gap: 10px; }

  /* Mobile nav toggle + overlay (hidden on desktop) */
  .ad-menu-btn { display: none; width: 32px; height: 32px; border-radius: 8px; border: 1px solid var(--border-hi); background: var(--bg-card); color: var(--text-secondary); align-items: center; justify-content: center; cursor: pointer; flex-shrink: 0; }
  .ad-menu-btn:hover { background: var(--accent-dim); color: var(--accent-light); }
  .ad-sidebar-overlay { display: none; }

  /* ── Responsive breakpoints ───────────────────────────────────────────── */

  /* Tablet / small desktop */
  /* Tablet: collapse sidebar to off-canvas */
  @media (max-width: 900px) {
    :root { --sidebar-w: 248px; }
    .ad-sidebar {
      position: fixed; top: 0; left: 0; bottom: 0; z-index: 40;
      height: 100dvh; min-height: 0;
      transform: translateX(-100%); transition: transform 0.22s ease;
      box-shadow: 0 0 40px rgba(0,0,0,0.5);
    }
    .ad-sidebar.mobile-open { transform: translateX(0); }
    .ad-sidebar-overlay.open {
      display: block; position: fixed; inset: 0; z-index: 35;
      background: rgba(0,0,0,0.55); backdrop-filter: blur(2px);
    }
    .ad-menu-btn { display: flex; }
    .mp-topbar { padding: 0 14px; }
    .mp-topbar-sub { display: none; }
    .mp-main { padding: 18px 14px; }
    .mp-form-grid-3 { grid-template-columns: 1fr 1fr; }
    .mp-form-grid-3 > div:last-child { grid-column: 1 / -1; }
  }

  /* Small tablet / large phone */
  @media (max-width: 680px) {
    .mp-stat-grid { gap: 10px; }
    .mp-stat { padding: 14px 16px; }
    .mp-stat-value { font-size: 21px; }
    .mp-topbar-title { font-size: 13.5px; }
    .mp-topbar-badge span, .mp-topbar-badge { font-size: 9px; }
    .mp-table-head { padding: 12px 14px; }
    .mp-search { width: 100%; }
    .mp-search-wrap { width: 100%; }
    .mp-table-head > div:last-child { width: 100%; }
    .mp-form-grid-2, .mp-form-grid-4 { grid-template-columns: 1fr 1fr; }
    .mp-modal-body { padding: 18px 16px; }
    .mp-modal-head { padding: 16px 18px 14px; }
    .mp-modal-foot { padding: 14px 18px; }
  }

  /* Phone */
  @media (max-width: 480px) {
    .mp-topbar { height: auto; min-height: var(--topbar-h); padding: 10px 12px; }
    .mp-topbar-left { gap: 10px; }
    .mp-topbar-badge { display: none; }
    .mp-topbar-right { gap: 6px; }
    .mp-btn span, .mp-btn { font-size: 10.5px; padding: 6px 10px; }
    .mp-btn-accent svg + *, .mp-btn svg + * {}
    .mp-th { padding: 8px 10px; font-size: 9px; }
    .mp-td { padding: 10px 10px; }
    .mp-form-grid-2, .mp-form-grid-3, .mp-form-grid-4 { grid-template-columns: 1fr; }
    .mp-form-grid-3 > div:last-child { grid-column: auto; }
    .mp-overlay { padding: 0; align-items: flex-end; }
    .mp-modal { border-radius: var(--radius-lg) var(--radius-lg) 0 0; max-height: 92vh; }
    .mp-pagination { flex-direction: column; align-items: flex-start; gap: 8px; }
  }
`;

function StyleInjector() {
  useEffect(() => {
    const el = document.createElement("style");
    el.textContent = STYLES;
    document.head.appendChild(el);
    return () => document.head.removeChild(el);
  }, []);
  return null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function fmtDate(str)     { if (!str) return "—"; return new Date(str).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }); }
function fmtDateTime(str) { if (!str) return "—"; return new Date(str).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
function fmtCurrency(val) { if (val == null) return "—"; return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 }).format(val); }
function userInitials(u)  { return (`${u?.first_name?.[0] ?? ""}${u?.last_name?.[0] ?? ""}`).toUpperCase() || "?"; }
function userName(u)      { return `${u?.first_name ?? ""} ${u?.last_name ?? ""}`.trim() || u?.email || "Unknown"; }
function slugify(str)     { return str.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, ""); }

const AVATAR_PALETTE = ["indigo", "green", "amber", "rose", "purple"];
function avatarColor(id)  { return AVATAR_PALETTE[(id?.charCodeAt?.(0) ?? 0) % AVATAR_PALETTE.length]; }

// ─── Sidebar link ─────────────────────────────────────────────────────────────
function SidebarLink({ to, label, icon: Icon, end }) {
  return (
    <NavLink to={to} end={end} className={({ isActive }) => `ad-nav-link${isActive ? " active" : ""}`}>
      {({ isActive }) => (
        <>
          <Icon size={14} />
          <span style={{ flex: 1 }}>{label}</span>
          {isActive && <ChevronRight size={10} className="ad-nav-chevron" />}
        </>
      )}
    </NavLink>
  );
}

function SortIcon({ field, sortField, sortDir }) {
  if (field !== sortField) return <ChevronUp size={9} style={{ opacity: 0.2, marginLeft: 4, display: "inline" }} />;
  return sortDir === 1
    ? <ChevronUp   size={9} style={{ color: "var(--accent-light)", marginLeft: 4, display: "inline" }} />
    : <ChevronDown size={9} style={{ color: "var(--accent-light)", marginLeft: 4, display: "inline" }} />;
}

// ─── Subscriptions modal ──────────────────────────────────────────────────────
function SubsModal({ item, onClose }) {
  const [subs, setSubs]       = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase
      .from("marketplace_subscriptions")
      .select("id, status, pnl_30d, started_at, renews_at, cancelled_at, users(id, first_name, last_name, email)")
      .eq("item_id", item.id)
      .order("started_at", { ascending: false })
      .then(({ data }) => { setSubs(data ?? []); setLoading(false); });
  }, [item.id]);

  const statusCls = (s) => s === "active" ? "mp-chip mp-chip-sub-active" : s === "cancelled" ? "mp-chip mp-chip-sub-cancelled" : "mp-chip mp-chip-sub-paused";

  return (
    <div className="mp-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="mp-modal" style={{ maxWidth: 520 }}>
        <div className="mp-modal-head">
          <div>
            <div className="mp-modal-title">Subscribers</div>
            <div className="mp-modal-sub">{item.name} · {subs.length} subscriber{subs.length !== 1 ? "s" : ""}</div>
          </div>
          <button className="mp-close-btn" onClick={onClose}><X size={13} /></button>
        </div>
        <div style={{ overflowY: "auto", maxHeight: "58vh" }}>
          {loading ? (
            <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 10 }}>
              {Array.from({ length: 4 }).map((_, i) => <div key={i} className="mp-skel" style={{ height: 52, borderRadius: 10 }} />)}
            </div>
          ) : subs.length === 0 ? (
            <div className="mp-empty">
              <div className="mp-empty-icon"><Users size={16} /></div>
              <span style={{ fontSize: 13, color: "var(--text-secondary)" }}>No subscribers yet</span>
            </div>
          ) : subs.map((s) => {
            const aColor = avatarColor(s.users?.id ?? "");
            return (
              <div key={s.id} className="mp-sub-row">
                <div className={`mp-avatar mp-avatar-${aColor}`}>{userInitials(s.users)}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)" }}>{userName(s.users)}</div>
                  <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>{s.users?.email}</div>
                </div>
                <div style={{ textAlign: "right", flexShrink: 0 }}>
                  <span className={statusCls(s.status)}>{s.status}</span>
                  <div style={{ fontSize: 10, color: "var(--text-muted)", marginTop: 4 }}>Since {fmtDate(s.started_at)}</div>
                  {s.pnl_30d !== 0 && (
                    <div style={{ fontSize: 10, color: s.pnl_30d >= 0 ? "var(--green)" : "var(--rose)", marginTop: 2, fontFamily: "var(--font-mono)" }}>
                      {s.pnl_30d >= 0 ? "+" : ""}{fmtCurrency(s.pnl_30d)} 30d P&L
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <div className="mp-modal-foot" style={{ justifyContent: "space-between" }}>
          <span style={{ fontSize: 11, color: "var(--text-muted)" }}>
            {subs.filter((s) => s.status === "active").length} active · {subs.filter((s) => s.status === "cancelled").length} cancelled
          </span>
          <button className="mp-ghost-btn" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ─── Item form modal (add / edit) ─────────────────────────────────────────────
function ItemFormModal({ item, onClose, onSuccess }) {
  const isEdit = !!item;
  const [saving, setSaving] = useState(false);
  const [err,    setErr]    = useState("");

  const [form, setForm] = useState({
    name:            item?.name            ?? "",
    slug:            item?.slug            ?? "",
    type:            item?.type            ?? "signal",
    tag:             item?.tag             ?? "",
    description:     item?.description    ?? "",
    price_monthly:   item?.price_monthly   != null ? String(item.price_monthly) : "",
    badge_label:     item?.badge_label     ?? "",
    badge_type:      item?.badge_type      ?? "",
    color_hex:       item?.color_hex       ?? "#6366f1",
    delivery_method: item?.delivery_method ?? "",
    markets:         item?.markets?.join(", ") ?? "",
    roi_12m_pct:     item?.roi_12m_pct     != null ? String(item.roi_12m_pct)  : "",
    win_rate_pct:    item?.win_rate_pct    != null ? String(item.win_rate_pct) : "",
    total_trades:    item?.total_trades    != null ? String(item.total_trades) : "",
    accuracy_pct:    item?.accuracy_pct    != null ? String(item.accuracy_pct) : "",
    is_active:       item?.is_active       ?? true,
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Auto-generate slug from name if adding new
  useEffect(() => {
    if (!isEdit && form.name) set("slug", slugify(form.name));
  }, [form.name]);

  async function handleSubmit() {
    setErr("");
    if (!form.name.trim()) return setErr("Name is required.");
    if (!form.slug.trim()) return setErr("Slug is required.");
    setSaving(true);
    const payload = {
      name:            form.name.trim(),
      slug:            form.slug.trim(),
      type:            form.type,
      tag:             form.tag.trim() || null,
      description:     form.description.trim() || null,
      price_monthly:   form.price_monthly !== "" ? parseFloat(form.price_monthly) : 0,
      badge_label:     form.badge_label.trim() || null,
      badge_type:      form.badge_type.trim()  || null,
      color_hex:       form.color_hex || null,
      delivery_method: form.delivery_method.trim() || null,
      markets:         form.markets ? form.markets.split(",").map((m) => m.trim()).filter(Boolean) : null,
      roi_12m_pct:     form.roi_12m_pct  !== "" ? parseFloat(form.roi_12m_pct)  : null,
      win_rate_pct:    form.win_rate_pct !== "" ? parseFloat(form.win_rate_pct) : null,
      total_trades:    form.total_trades !== "" ? parseInt(form.total_trades)   : null,
      accuracy_pct:    form.accuracy_pct !== "" ? parseFloat(form.accuracy_pct) : null,
      is_active:       form.is_active,
    };
    let error;
    if (isEdit) {
      ({ error } = await supabase.from("marketplace_items").update(payload).eq("id", item.id));
    } else {
      ({ error } = await supabase.from("marketplace_items").insert(payload));
    }
    setSaving(false);
    if (error) return setErr(error.message);
    onSuccess(isEdit ? "Item updated." : "Item created.");
    onClose();
  }

  return (
    <div className="mp-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="mp-modal" style={{ maxWidth: 580 }}>
        <div className="mp-modal-head">
          <div>
            <div className="mp-modal-title">{isEdit ? "Edit item" : "Add marketplace item"}</div>
            <div className="mp-modal-sub">{isEdit ? `Editing: ${item.name}` : "Fill in the details below"}</div>
          </div>
          <button className="mp-close-btn" onClick={onClose}><X size={13} /></button>
        </div>

        <div className="mp-modal-body">
          {err && (
            <div style={{ padding: "10px 14px", borderRadius: 8, background: "var(--rose-dim)", border: "1px solid rgba(251,113,133,0.25)", fontSize: 12, color: "var(--rose)" }}>
              {err}
            </div>
          )}

          {/* Basic info */}
          <div className="mp-section-label">Basic info</div>
          <div className="mp-form-grid-2">
            <div className="mp-form-row">
              <label className="mp-form-label">Name *</label>
              <input className="mp-form-input" placeholder="e.g. Gold Signal Pro" value={form.name} onChange={(e) => set("name", e.target.value)} />
            </div>
            <div className="mp-form-row">
              <label className="mp-form-label">Slug *</label>
              <input className="mp-form-input" placeholder="auto-generated" value={form.slug} onChange={(e) => set("slug", e.target.value)} style={{ fontFamily: "var(--font-mono)", fontSize: 11.5 }} />
            </div>
          </div>

          {/* Type */}
          <div className="mp-form-row">
            <label className="mp-form-label">Type *</label>
            <div className="mp-type-pills">
              {ITEM_TYPES.map((t) => {
                const meta = TYPE_META[t];
                return (
                  <button key={t} className={`mp-type-pill${form.type === t ? " active" : ""}`}
                    onClick={() => set("type", t)}>
                    {meta.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mp-form-grid-2">
            <div className="mp-form-row">
              <label className="mp-form-label">Tag</label>
              <input className="mp-form-input" placeholder="e.g. #1 Signal" value={form.tag} onChange={(e) => set("tag", e.target.value)} />
            </div>
            <div className="mp-form-row">
              <label className="mp-form-label">Price / month (USD)</label>
              <input className="mp-form-input" type="number" min="0" step="0.01" placeholder="0.00" value={form.price_monthly} onChange={(e) => set("price_monthly", e.target.value)} />
            </div>
          </div>

          <div className="mp-form-row">
            <label className="mp-form-label">Description</label>
            <textarea className="mp-form-textarea" placeholder="Describe this marketplace item…" value={form.description} onChange={(e) => set("description", e.target.value)} />
          </div>

          {/* Badge + visuals */}
          <div className="mp-section-label">Badge & visuals</div>
          <div className="mp-form-grid-3">
            <div className="mp-form-row">
              <label className="mp-form-label">Badge label</label>
              <input className="mp-form-input" placeholder="e.g. Best Seller" value={form.badge_label} onChange={(e) => set("badge_label", e.target.value)} />
            </div>
            <div className="mp-form-row">
              <label className="mp-form-label">Badge type</label>
              <select className="mp-form-select" value={form.badge_type} onChange={(e) => set("badge_type", e.target.value)}>
                <option value="">None</option>
                <option value="hot">Hot</option>
                <option value="new">New</option>
                <option value="popular">Popular</option>
                <option value="recommended">Recommended</option>
              </select>
            </div>
            <div className="mp-form-row">
              <label className="mp-form-label">Color</label>
              <input type="color" className="mp-form-input" style={{ padding: "4px 6px", height: 36, cursor: "pointer" }} value={form.color_hex} onChange={(e) => set("color_hex", e.target.value)} />
            </div>
          </div>

          {/* Delivery + Markets */}
          <div className="mp-section-label">Delivery & coverage</div>
          <div className="mp-form-grid-2">
            <div className="mp-form-row">
              <label className="mp-form-label">Delivery method</label>
              <input className="mp-form-input" placeholder="e.g. Telegram, Email" value={form.delivery_method} onChange={(e) => set("delivery_method", e.target.value)} />
            </div>
            <div className="mp-form-row">
              <label className="mp-form-label">Markets (comma-separated)</label>
              <input className="mp-form-input" placeholder="e.g. EURUSD, BTCUSD" value={form.markets} onChange={(e) => set("markets", e.target.value)} />
            </div>
          </div>

          {/* Performance */}
          <div className="mp-section-label">Performance stats</div>
          <div className="mp-form-grid-4">
            {[
              ["ROI 12m %",    "roi_12m_pct",  "e.g. 124.5"],
              ["Win rate %",   "win_rate_pct", "e.g. 72.3"],
              ["Total trades", "total_trades", "e.g. 340"],
              ["Accuracy %",   "accuracy_pct", "e.g. 68.0"],
            ].map(([label, key, ph]) => (
              <div key={key} className="mp-form-row">
                <label className="mp-form-label">{label}</label>
                <input className="mp-form-input" type="number" step="any" min="0" placeholder={ph}
                  value={form[key]} onChange={(e) => set(key, e.target.value)} />
              </div>
            ))}
          </div>

          {/* Status */}
          <div className="mp-form-row">
            <label className="mp-form-label">Status</label>
            <div style={{ display: "flex", gap: 6 }}>
              {[true, false].map((val) => (
                <button key={String(val)}
                  className={`mp-type-pill${form.is_active === val ? " active" : ""}`}
                  style={form.is_active === val && val ? { borderColor: "rgba(52,211,153,0.4)", background: "var(--green-dim)", color: "var(--green)" } : {}}
                  onClick={() => set("is_active", val)}>
                  {val ? "Active" : "Inactive"}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="mp-modal-foot">
          <button className="mp-ghost-btn" onClick={onClose}>Cancel</button>
          <button className="mp-save-btn" onClick={handleSubmit} disabled={saving}>
            {saving ? (isEdit ? "Saving…" : "Creating…") : (isEdit ? "Save changes" : "Create item")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Confirm delete modal ─────────────────────────────────────────────────────
function ConfirmModal({ message, onConfirm, onClose, loading }) {
  return (
    <div className="mp-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="mp-modal" style={{ maxWidth: 400 }}>
        <div className="mp-modal-head">
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ width: 36, height: 36, borderRadius: "50%", background: "var(--rose-dim)", border: "1px solid rgba(251,113,133,0.25)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Trash2 size={15} style={{ color: "var(--rose)" }} />
            </div>
            <div className="mp-modal-title">Confirm deletion</div>
          </div>
          <button className="mp-close-btn" onClick={onClose}><X size={13} /></button>
        </div>
        <div className="mp-modal-body">
          <p style={{ fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.6 }}>{message}</p>
        </div>
        <div className="mp-modal-foot">
          <button className="mp-ghost-btn" onClick={onClose}>Cancel</button>
          <button className="mp-danger-btn" onClick={onConfirm} disabled={loading}>
            {loading ? "Deleting…" : "Delete item"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function AdminMarketplace() {
  const navigate = useNavigate();

  const [adminEmail,    setAdminEmail]    = useState("");
  const [adminInitials, setAdminInitials] = useState("A");
  const [sessionReady,  setSessionReady]  = useState(false);

  const [items,      setItems]      = useState([]);
  const [subCounts,  setSubCounts]  = useState({});
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [sortField,  setSortField]  = useState("created_at");
  const [sortDir,    setSortDir]    = useState(-1);
  const [page,       setPage]       = useState(1);

  const [showAdd,    setShowAdd]    = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [editItem,   setEditItem]   = useState(null);
  const [viewSubs,   setViewSubs]   = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);
  const [deleting,   setDeleting]   = useState(false);
  const [toast,      setToast]      = useState({ msg: "", ok: true });

  // ── Session gate ────────────────────────────────────────────────────────
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) { navigate("/admin/login", { replace: true }); return; }
      const email = session.user?.email ?? "";
      setAdminEmail(email);
      const parts = email.split("@")[0].split(".");
      setAdminInitials((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? parts[0]?.[1] ?? ""));
      setSessionReady(true);
    });
  }, [navigate]);

  async function handleLogout() {
    await supabase.auth.signOut();
    navigate("/admin/login", { replace: true });
  }

  // ── Fetch ───────────────────────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    setLoading(true);
    const [{ data: i }, { data: s }] = await Promise.all([
      supabase.from("marketplace_items")
        .select("id, slug, type, name, tag, description, price_monthly, badge_label, badge_type, color_hex, delivery_method, markets, roi_12m_pct, win_rate_pct, total_trades, accuracy_pct, subscriber_count, is_active, created_at")
        .order("created_at", { ascending: false }),
      supabase.from("marketplace_subscriptions").select("item_id"),
    ]);
    setItems(i ?? []);
    const counts = {};
    (s ?? []).forEach(({ item_id }) => { counts[item_id] = (counts[item_id] ?? 0) + 1; });
    setSubCounts(counts);
    setLoading(false);
  }, []);

  useEffect(() => { if (sessionReady) fetchAll(); }, [sessionReady, fetchAll]);

  function showToast(msg, ok = true) {
    setToast({ msg, ok });
    setTimeout(() => setToast({ msg: "", ok: true }), 3200);
  }

  async function handleDelete() {
    if (!deleteItem) return;
    setDeleting(true);
    await supabase.from("marketplace_subscriptions").delete().eq("item_id", deleteItem.id);
    const { error } = await supabase.from("marketplace_items").delete().eq("id", deleteItem.id);
    setDeleting(false);
    if (error) return showToast(error.message, false);
    showToast("Item deleted.");
    setDeleteItem(null);
    fetchAll();
  }

  // ── Filter + sort ───────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    let list = items.filter((i) => {
      const matchQ = !q || i.name?.toLowerCase().includes(q) || i.description?.toLowerCase().includes(q) || i.tag?.toLowerCase().includes(q) || i.type?.toLowerCase().includes(q);
      const matchT = typeFilter === "all" || i.type === typeFilter;
      return matchQ && matchT;
    });
    list.sort((a, b) => {
      if (sortField === "name")          return (a.name ?? "").localeCompare(b.name ?? "") * sortDir;
      if (sortField === "price_monthly") return ((a.price_monthly ?? 0) - (b.price_monthly ?? 0)) * sortDir;
      if (sortField === "subs")          return ((subCounts[a.id] ?? 0) - (subCounts[b.id] ?? 0)) * sortDir;
      return (new Date(a.created_at ?? 0) - new Date(b.created_at ?? 0)) * sortDir;
    });
    return list;
  }, [items, search, typeFilter, sortField, sortDir, subCounts]);

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const pageSlice  = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  function handleSort(field) {
    if (field === sortField) setSortDir((d) => d * -1);
    else { setSortField(field); setSortDir(-1); }
    setPage(1);
  }

  // ── Stats ───────────────────────────────────────────────────────────────
  const stats = useMemo(() => ({
    total:     items.length,
    active:    items.filter((i) => i.is_active).length,
    totalSubs: Object.values(subCounts).reduce((a, b) => a + b, 0),
    signals:   items.filter((i) => i.type === "signal").length,
    bots:      items.filter((i) => i.type === "bot").length,
    revenue:   items.reduce((s, i) => s + (i.price_monthly ?? 0) * (subCounts[i.id] ?? 0), 0),
  }), [items, subCounts]);

  const STAT_CARDS = [
    { label: "Total items",   value: stats.total,             icon: ShoppingBag, bg: "rgba(99,102,241,0.15)",  color: "var(--accent-light)", glow: "rgba(99,102,241,0.08)",  bar: "var(--accent)",  sub: `${stats.active} active`        },
    { label: "Subscriptions", value: stats.totalSubs,         icon: Users,       bg: "rgba(167,139,250,0.15)", color: "var(--purple)",       glow: "rgba(167,139,250,0.08)", bar: "var(--purple)",  sub: "Across all items"              },
    { label: "Signals / Bots",value: `${stats.signals} / ${stats.bots}`, icon: Activity, bg: "rgba(52,211,153,0.15)", color: "var(--green)", glow: "rgba(52,211,153,0.08)",  bar: "var(--green)",   sub: "Active product types"          },
    { label: "MRR (est.)",    value: fmtCurrency(stats.revenue), icon: TrendingUp, bg: "rgba(251,191,36,0.15)", color: "var(--amber)",       glow: "rgba(251,191,36,0.08)",  bar: "var(--amber)",   sub: "Monthly recurring revenue"     },
  ];

  const SORT_COLS  = [
    { key: "name",          label: "Item"        },
    { key: "created_at",    label: "Added"       },
    { key: "price_monthly", label: "Price/mo"    },
    { key: "subs",          label: "Subscribers" },
  ];
  const STATIC_COLS = ["Type", "Status", "Actions"];

  if (!sessionReady) {
    return (
      <div className="mp-gate">
        <StyleInjector />
        <div className="mp-gate-icon"><ShoppingBag size={16} color="#fff" /></div>
        <p style={{ fontSize: 12, color: "var(--text-muted)", fontFamily: "Inter, sans-serif" }}>Verifying session…</p>
      </div>
    );
  }

  return (
    <div className="mp-shell">
      <StyleInjector />

      {/* ── Sidebar ── */}
      <aside className={`ad-sidebar${mobileNavOpen ? " mobile-open" : ""}`}>
        <div className="ad-logo">
          <div className="ad-logo-icon"><CandlestickChart size={16} color="#fff" /></div>
          <div className="ad-logo-text">
            <strong>vantrexmarket</strong>
            <span>Admin panel</span>
          </div>
          <button className="ad-menu-btn" style={{ marginLeft: "auto" }} onClick={() => setMobileNavOpen(false)} title="Close menu">
            <X size={15} />
          </button>
        </div>
        <nav className="ad-nav">
          {NAV_SECTIONS.map((section) => (
            <div key={section.label}>
              <p className="ad-nav-section-label">{section.label}</p>
              <div className="ad-nav-items" onClick={() => setMobileNavOpen(false)}>
                {section.items.map((item) => <SidebarLink key={item.to} {...item} />)}
              </div>
            </div>
          ))}
        </nav>
        <div className="ad-user-card">
          <div className="ad-user-inner">
            <div className="ad-user-avatar">{adminInitials.toUpperCase()}</div>
            <div className="ad-user-info">
              <strong>Super Admin</strong>
              <span>{adminEmail}</span>
            </div>
            <button className="ad-logout-btn" onClick={handleLogout} title="Logout"><LogOut size={13} /></button>
          </div>
        </div>
      </aside>

      {/* Mobile sidebar backdrop */}
      <div className={`ad-sidebar-overlay${mobileNavOpen ? " open" : ""}`} onClick={() => setMobileNavOpen(false)} />

      {/* ── Page ── */}
      <div className="mp-page">

        {/* Topbar */}
        <header className="mp-topbar">
          <div className="mp-topbar-left">
            <button className="ad-menu-btn" onClick={() => setMobileNavOpen(true)} title="Open menu">
              <Menu size={15} />
            </button>
            <div className="mp-topbar-badge"><ShoppingBag size={9} />Marketplace</div>
            <div>
              <div className="mp-topbar-title">Marketplace management</div>
              <div className="mp-topbar-sub">Manage signals, bots, indicators and strategies</div>
            </div>
          </div>
          <div className="mp-topbar-right">
            <button className="mp-btn" onClick={fetchAll}><RefreshCw size={12} />Refresh</button>
            <button className="mp-btn mp-btn-accent" onClick={() => setShowAdd(true)}><Plus size={12} />Add item</button>
          </div>
        </header>

        <main className="mp-main">

          {/* Stat cards */}
          <div className="mp-stat-grid">
            {STAT_CARDS.map(({ label, value, icon: Icon, bg, color, glow, bar, sub }) => (
              <div key={label} className="mp-stat" style={{ "--stat-glow": glow, "--stat-bar": bar }}>
                <div className="mp-stat-top">
                  <span className="mp-stat-label">{label}</span>
                  <div className="mp-stat-icon" style={{ background: bg }}><Icon size={14} style={{ color }} /></div>
                </div>
                <div>
                  <div className="mp-stat-value">{value}</div>
                  {sub && <div className="mp-stat-sub">{sub}</div>}
                </div>
              </div>
            ))}
          </div>

          {/* Table */}
          <div className="mp-table-card">
            <div className="mp-table-head">
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span className="mp-table-title">All items</span>
                <span className="mp-table-count">{filtered.length}</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                {/* Type filter */}
                <div className="mp-filter-wrap">
                  {["all", ...ITEM_TYPES].map((t) => (
                    <button key={t} className={`mp-filter-pill${typeFilter === t ? " active" : ""}`}
                      onClick={() => { setTypeFilter(t); setPage(1); }}>
                      {t}
                    </button>
                  ))}
                </div>
                {/* Search */}
                <div className="mp-search-wrap">
                  <Search size={12} className="mp-search-icon" />
                  <input type="text" className="mp-search" placeholder="Search items…"
                    value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
                </div>
              </div>
            </div>

            <div style={{ overflowX: "auto" }}>
              <table className="mp-table">
                <thead>
                  <tr>
                    {SORT_COLS.map(({ key, label }) => (
                      <th key={key} className="mp-th" onClick={() => handleSort(key)}>
                        {label}<SortIcon field={key} sortField={sortField} sortDir={sortDir} />
                      </th>
                    ))}
                    {STATIC_COLS.map((h) => (
                      <th key={h} className={`mp-th no-sort${h === "Actions" ? " right" : ""}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    Array.from({ length: 7 }).map((_, i) => (
                      <tr key={i} className="mp-tr">
                        {Array.from({ length: 7 }).map((_, j) => (
                          <td key={j} className="mp-td">
                            <div className="mp-skel" style={{ height: 12, width: `${40 + Math.random() * 40}%` }} />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : pageSlice.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="mp-td">
                        <div className="mp-empty">
                          <div className="mp-empty-icon"><ShoppingBag size={16} /></div>
                          <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-secondary)" }}>No items found</span>
                          {search && <span style={{ fontSize: 11 }}>Try clearing your search filter</span>}
                        </div>
                      </td>
                    </tr>
                  ) : pageSlice.map((i) => {
                    const meta     = TYPE_META[i.type] ?? TYPE_META.other;
                    const TypeIcon = meta.Icon;
                    const typeColor= i.color_hex ?? meta.color;
                    const subCount = subCounts[i.id] ?? i.subscriber_count ?? 0;
                    return (
                      <tr key={i.id} className="mp-tr">
                        {/* Item */}
                        <td className="mp-td">
                          <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
                            <div className="mp-item-icon" style={{ background: `${typeColor}22` }}>
                              <TypeIcon size={15} style={{ color: typeColor }} />
                            </div>
                            <div>
                              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)" }}>
                                {i.name}
                                {i.badge_label && (
                                  <span style={{ marginLeft: 6, fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 99, background: `${typeColor}22`, color: typeColor, verticalAlign: "middle" }}>
                                    {i.badge_label}
                                  </span>
                                )}
                              </div>
                              <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
                                {i.tag || i.description?.slice(0, 48) || i.slug}
                              </div>
                            </div>
                          </div>
                        </td>
                        {/* Added */}
                        <td className="mp-td">
                          <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>{fmtDate(i.created_at)}</span>
                        </td>
                        {/* Price */}
                        <td className="mp-td">
                          <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text-primary)", fontFamily: "var(--font-mono)" }}>
                            {i.price_monthly > 0 ? fmtCurrency(i.price_monthly) : <span style={{ color: "var(--green)", fontSize: 11, fontWeight: 600 }}>Free</span>}
                          </span>
                        </td>
                        {/* Subscribers */}
                        <td className="mp-td">
                          <button style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: "var(--text-secondary)", background: "none", border: "none", cursor: "pointer", padding: 0, transition: "color 0.15s" }}
                            onMouseEnter={(e) => e.currentTarget.style.color = "var(--accent-light)"}
                            onMouseLeave={(e) => e.currentTarget.style.color = "var(--text-secondary)"}
                            onClick={() => setViewSubs(i)}>
                            <Users size={11} />{subCount}
                          </button>
                        </td>
                        {/* Type */}
                        <td className="mp-td">
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 99, fontSize: 10, fontWeight: 600, background: `${typeColor}18`, color: typeColor, border: `1px solid ${typeColor}33` }}>
                            <TypeIcon size={9} />{meta.label}
                          </span>
                        </td>
                        {/* Status */}
                        <td className="mp-td">
                          <span className={`mp-chip ${i.is_active ? "mp-chip-active" : "mp-chip-inactive"}`}>
                            {i.is_active ? <CheckCircle2 size={9} /> : <XCircle size={9} />}
                            {i.is_active ? "Active" : "Inactive"}
                          </span>
                        </td>
                        {/* Actions */}
                        <td className="mp-td" style={{ textAlign: "right" }}>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 4 }}>
                            <button className="mp-icon-btn" title="View subscribers" onClick={() => setViewSubs(i)}><Eye size={13} /></button>
                            <button className="mp-icon-btn edit" title="Edit item" onClick={() => setEditItem(i)}><Pencil size={13} /></button>
                            <button className="mp-icon-btn delete" title="Delete item" onClick={() => setDeleteItem(i)}><Trash2 size={13} /></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="mp-pagination">
                <span className="mp-page-info">
                  Showing {(page - 1) * PER_PAGE + 1}–{Math.min(page * PER_PAGE, filtered.length)} of {filtered.length}
                </span>
                <div className="mp-page-btns">
                  {Array.from({ length: Math.min(totalPages, 8) }, (_, i) => i + 1).map((p) => (
                    <button key={p} className={`mp-page-btn${page === p ? " active" : ""}`} onClick={() => setPage(p)}>{p}</button>
                  ))}
                </div>
              </div>
            )}
          </div>

        </main>
      </div>

      {/* ── Modals ── */}
      {showAdd     && <ItemFormModal item={null}      onClose={() => setShowAdd(false)}    onSuccess={(m) => { showToast(m); fetchAll(); }} />}
      {editItem    && <ItemFormModal item={editItem}  onClose={() => setEditItem(null)}    onSuccess={(m) => { showToast(m); fetchAll(); }} />}
      {viewSubs    && <SubsModal    item={viewSubs}  onClose={() => setViewSubs(null)} />}
      {deleteItem  && <ConfirmModal message={`Delete "${deleteItem.name}"? All subscriptions will also be removed.`} onConfirm={handleDelete} onClose={() => setDeleteItem(null)} loading={deleting} />}

      {/* Toast */}
      {toast.msg && (
        <div className="mp-toast">
          {toast.ok
            ? <CheckCircle2 size={15} style={{ color: "var(--green)", flexShrink: 0 }} />
            : <AlertCircle  size={15} style={{ color: "var(--rose)",  flexShrink: 0 }} />}
          {toast.msg}
        </div>
      )}
    </div>
  );
}